// Tech and Automation - job cards (step-by-step instructions), keyed by job code.
// A step is { text, tag }; a red STOP bar is { stop }. Loaded automatically by constants/jobCards.js.

export const division = "Tech and Automation";

export const cards = {
  T1: {
    title: "Set up tracking and prove it works",
    before: [
      "Access to the site, the tag manager, analytics and the client's CRM",
      "Written list of what counts as a result",
    ],
    steps: [
      { text: "Agree in writing what actions count as a result, and which ONE matters most", tag: "Conversion list" },
      { text: "Make sure the same action has the same name everywhere", tag: "Naming agreed" },
      { text: "Install the tags through the tag manager, not directly in the site code", tag: "Tags live" },
      { text: "Set up the conversion actions in each platform", tag: "Conversions set" },
      { text: "Set up server-side where the client needs it", tag: "Server-side" },
      { text: "Apply the UTM convention to every link the team will use", tag: "UTMs" },
      { text: "Submit a REAL test conversion yourself", tag: "Test submitted" },
      { text: "Confirm it appears in the ad platform, in analytics, AND in the client's CRM", tag: "All three ok" },
      { text: "Screenshot all three with the timestamp and save them in the client folder", tag: "Evidence filed" },
      { text: "Check nothing is being counted twice", tag: "No duplicates" },
      { text: "Tell the division that asked: tracking is verified, here is the evidence", tag: "Handed back" },
      { text: "Book the monthly re-test. Site changes break tags silently", tag: "Re-test booked" },
    ],
    finishedWhen: "A real test conversion reached all three places, with screenshots saved and the asking division told.",
  },
  T2: {
    title: "Set up a CRM",
    before: [
      "Access to the client's sales team",
      "Scope agreed",
    ],
    steps: [
      { text: "Map how they ACTUALLY sell, before configuring anything. A CRM built against an imagined process gets abandoned.", tag: "Process map" },
      { text: "Set up only the fields someone will actually fill in", tag: "Fields" },
      { text: "Set the pipeline stages, and agree with sales what moves a deal from one to the next", tag: "Stages" },
      { text: "Set up lead sources so we can tell where a lead came from", tag: "Sources" },
      { text: "Connect the forms and set up where each lead goes", tag: "Routing" },
      { text: "Set up the automatic bits: assigning, notifications, tasks", tag: "Automation" },
      { text: "Build the reports and dashboards they asked for", tag: "Reports" },
      { text: "Move the old data across and remove duplicates", tag: "Migrated" },
      { text: "Test the whole thing end to end with REAL records", tag: "Tested" },
      { text: "Train them and write a short guide. A CRM nobody was trained on is just a database", tag: "Trained" },
    ],
    finishedWhen: "CRM matches how they really sell, tested with real records, and the team has been trained.",
  },
  T3: {
    title: "Connect tools together",
    before: [
      "Clear list of what should move from where to where",
    ],
    steps: [
      { text: "Write down exactly what moves: from which tool, to which tool, and when", tag: "Map" },
      { text: "Decide what happens when one side is down or slow", tag: "Failure plan" },
      { text: "Build it", tag: "Built" },
      { text: "Test it end to end with REAL data, not a dummy record", tag: "Tested" },
      { text: "SET UP A FAILURE ALERT. Silent failures are how leads disappear between systems", tag: "Alert live" },
      { text: "Write down how it works, so someone else could fix it", tag: "Documented" },
      { text: "Tell the division that asked, and show them where the alert goes", tag: "Handed back" },
    ],
    finishedWhen: "Connection works with real data, has a failure alert, and is written down.",
  },
  T4: {
    title: "Domains, hosting and DNS",
    before: [
      "Access to the client's registrar and host, or a named contact who has it",
    ],
    steps: [
      { text: "CLIENTS OWN THEIR DOMAINS. We watch them, we cannot renew them", tag: "Understood" },
      { text: "Get delegated access. If the client will not give it, log that as a risk with their contact's name", tag: "Access or risk" },
      { text: "Set up the DNS records, SSL, and the email records", tag: "Records set" },
      { text: "Put EVERY client domain and certificate in the expiry calendar", tag: "Calendar" },
      { text: "Send a renewal notice at 60 days, 30 days and 7 days", tag: "Notices sent" },
      { text: "At 7 days, CALL them. An email nobody read is not a warning", tag: "Called" },
      { text: "If something does lapse, write down the timeline of notices the same day", tag: "Record" },
    ],
    finishedWhen: "Every domain and certificate is in the calendar, with three notices sent before expiry.",
  },
  T5: {
    title: "Dashboards and attribution",
    before: [
      "Agreed with the client how they want results credited",
      "Access to every source",
    ],
    steps: [
      { text: "Agree with the client HOW results get credited to channels", tag: "Model agreed" },
      { text: "Connect every source: ad platforms, analytics, CRM, email", tag: "Connected" },
      { text: "Build the view", tag: "Dashboard" },
      { text: "CHECK THE NUMBERS AGAINST THE PLATFORMS before anyone sees it. A dashboard that disagrees with the platforms is worse than no dashboard.", tag: "Verified" },
      { text: "Share access and show the division how to read it", tag: "Handed over" },
    ],
    finishedWhen: "Dashboard matches the source platforms and the division knows how to read it.",
  },
  T6: {
    title: "Build a custom tool or dashboard",
    before: [
      "A written problem, not a technology someone wants to use",
    ],
    steps: [
      { text: "Write down what actually breaks today, who it affects, and how often. Custom builds fail when they start from a technology instead of a problem.", tag: "Problem" },
      { text: "Check whether an existing tool, configured properly, would do 80% of it. If it would, SAY SO. Recommending a build we get paid for when configuration would do is bad advice.", tag: "Buy or build" },
      { text: "Write what it must do, and what it will NOT do", tag: "Scope" },
      { text: "Map what it reads, what it writes, and what happens if a source is unavailable", tag: "Data map" },
      { text: "Agree BEFORE building: who hosts it, who maintains it, who owns it at the end", tag: "Terms agreed" },
      { text: "Build a small working version first, not a complete one", tag: "Phase one" },
      { stop: "The people who will use it daily approve phase one. Not their boss" },
      { text: "Test with real data, including what happens when things go wrong", tag: "Tested" },
      { text: "Write the guide and train them", tag: "Trained" },
    ],
    finishedWhen: "A working first version, reviewed by real users, documented, with handover terms agreed.",
  },
  T7: {
    title: "Something broke — what to do",
    before: [
      "Nothing. Do this the moment you notice",
    ],
    steps: [
      { text: "Work out in 5 minutes: what broke, which client, and is anything being LOST right now", tag: "Assessed" },
      { text: "If leads are being lost, that is the most urgent kind. Tell the affected Division Lead NOW", tag: "Told" },
      { text: "If a client site or domain is down, tell the Division Lead and the founders the same day", tag: "Escalated" },
      { text: "Roll back first if you can. Diagnose after", tag: "Rolled back" },
      { text: "Fix it, then test it the same way you would a new build", tag: "Fixed" },
      { text: "Tell the division it is fixed, and what they should check on their side", tag: "Confirmed" },
      { text: "Within 24 hours write down what caused it and what would have caught it earlier", tag: "Cause noted" },
      { text: "Add that check to the monthly list", tag: "Prevented" },
    ],
    finishedWhen: "Fixed, the affected division told, and the check that would have caught it added to the monthly list.",
  },
  T8: {
    title: "Monthly checks",
    before: [
      "The client list",
    ],
    steps: [
      { text: "Re-test tracking on every live client with a real conversion", tag: "Tracking ok" },
      { text: "Check every connection between tools is still running", tag: "Integrations ok" },
      { text: "Check every failure alert is still switched on", tag: "Alerts ok" },
      { text: "Check the domain and certificate expiry calendar and send the notices due", tag: "Notices sent" },
      { text: "Check who has access to what, and remove anyone who has left", tag: "Access clean" },
      { text: "Check the dashboards still match the platforms", tag: "Dashboards ok" },
      { text: "Write a short note to the Operations Manager on what you found", tag: "Note sent" },
    ],
    finishedWhen: "All six checks done across every client, and the findings are with Operations.",
  },
  F1: {
    title: "Client setup — before any technical work",
    before: [
      "A new client, or a client where something below is missing",
    ],
    steps: [
      { text: "We have our own access, not the client's login", tag: "Access" },
      { text: "We know who at the client can grant access when we need more", tag: "Contact" },
      { text: "It is written down what counts as a result for this client", tag: "Conversions" },
      { text: "We know where their leads are supposed to end up", tag: "Destination" },
      { text: "We know who owns their domain and hosting", tag: "Ownership" },
      { text: "IF ANYTHING IS MISSING: tell the Division Lead. It becomes a job with a date", tag: "Raised" },
    ],
    finishedWhen: "All five exist and are saved in the client's Foundation folder.",
  },
  M1: {
    title: "The agency data standards",
    before: [
      "Agreement from all Division Leads",
    ],
    steps: [
      { text: "Write the UTM convention: exactly how every link is tagged", tag: "UTM rules" },
      { text: "Write the campaign naming convention", tag: "Naming rules" },
      { text: "Write the list of lead sources everyone uses", tag: "Source list" },
      { text: "Get every Division Lead to agree it", tag: "Agreed" },
      { text: "Publish it where everyone can find it, and put it in every relevant card. This is the single thing that makes cross-channel reporting trustworthy.", tag: "Published" },
    ],
    finishedWhen: "Published, agreed by every Division Lead, and in use.",
  },
  M2: {
    title: "Access, passwords and security",
    before: [
      "A password manager the agency actually uses",
    ],
    steps: [
      { text: "Never store a client credential outside the password manager", tag: "Stored safely" },
      { text: "Never use a client's personal login. Always request our own access", tag: "Own access" },
      { text: "Keep a list of what access we hold for each client", tag: "Access list" },
      { text: "Review it monthly and remove anyone who has left", tag: "Reviewed" },
      { text: "On offboarding, remove our access AFTER confirming the client has theirs", tag: "Clean exit" },
    ],
    finishedWhen: "Access list current, nothing stored outside the password manager.",
  },
  M3: {
    title: "Technical support for a website build",
    before: [
      "Growth Systems owns the website. We supply the technical parts",
    ],
    steps: [
      { text: "Take the request from Growth Systems with 5 working days notice", tag: "Request" },
      { text: "Set up the domain, DNS and certificate", tag: "Domain live" },
      { text: "Install the tracking and prove it fires on the live site", tag: "Tracking live" },
      { text: "Connect the forms to wherever the leads must go", tag: "Forms connected" },
      { text: "Check the redirects go live in the SAME change as the switch", tag: "Redirects" },
      { text: "Load the finished site fresh and confirm what fires and when", tag: "Verified" },
    ],
    finishedWhen: "Domain, tracking, forms and redirects all working on the live site, and confirmed to Growth Systems.",
  },
  M4: {
    title: "Handing a client's systems back",
    before: [
      "Client is leaving or moving the work",
    ],
    steps: [
      { text: "List everything we set up and everything we hold access to", tag: "Inventory" },
      { text: "Export anything that belongs to them", tag: "Exports" },
      { text: "Write a short note on how each thing is set up", tag: "Handover note" },
      { text: "Give them proper admin access", tag: "Access given" },
      { text: "Remove our access AFTER they confirm theirs works", tag: "Removed" },
      { stop: "Founders approve the handover" },
    ],
    finishedWhen: "Client has access, exports and a handover note. Our access is removed.",
  },
};
